/* eslint-disable eqeqeq */
import { Injectable } from '@angular/core';
import { AdminPortalFeaturesService } from '@workflow/ecp-shared/service/user/admin-portal-features.service';
import { getPageIterationId, processPath } from '../utilities.service';
import { StatefulValue } from '@workflow/class/stateful-value.class';
import { LogicFnContext } from '@workflow/service/logic/logic.service';
import { AlloyService } from '@workflow/ecp-workflow/dynamic-app/service/alloy/alloy.service';
import { StateManagementService } from '@workflow/service/state-management/state-management.service';
import { PrimitiveType } from '@workflow/class/workflow.type';
import { StatefulService } from '@app/service/stateful.service';
import { UserService } from '@workflow/ecp-shared/service/user/user.service';
import { CardService } from '@workflow/class/card.service';

/**
 * Methods on this service get registered as "logic functions" on LogicService
 */
@Injectable({ providedIn: 'root' })
export class OlaPageValidationFunctionsService {
  constructor(
    private readonly adminPortalFeaturesService: AdminPortalFeaturesService,
    private readonly stateManager: StateManagementService,
    private readonly alloySvc: AlloyService,
    private readonly statefulSvc: StatefulService,
    private readonly userSvc: UserService,
    private readonly cardService: CardService
  ) {}

  /** Get selected loan group admin portal configuration */
  public selectedLoanGroupConfig() {
    const selectedLoanGroupName = this.statefulSvc.wrap('ola:olaApplicationInformation/loanGroupName').valueString;
    const selectedLoanGroup = this.adminPortalFeaturesService?.config?.olaConfiguration?.consumerLoanGroups?.find(
      (l) => l.loanGroup === selectedLoanGroupName
    );
    return selectedLoanGroup;
  }

  /** Get selected loan product admin portal configuration */
  public selectedLoanProductConfig() {
    const selectedLoanGroup = this.selectedLoanGroupConfig();
    const selectedLoanProductName = this.statefulSvc.wrap('ola:olaApplicationInformation/loanProductName').valueString;
    const selectedLoanProduct = selectedLoanGroup?.loanProducts?.find((p) => p.productName === selectedLoanProductName);
    return selectedLoanProduct;
  }

  /**
   * DOB and Residence year validation
   *
   * @param _context The context object containing iteration and page information.
   * @returns `true` if the residence years are valid; otherwise, `false`
   */
  public isValidResidenceLivingYear = (_context: LogicFnContext): boolean => {
    const pageIterationId = getPageIterationId(_context);
    const protocol = pageIterationId != undefined ? `ola:coBorrowers/${pageIterationId}/` : 'ola:';
    const dobStoragePath = `${protocol}borrowerInfo/dateOfBirth`;

    const dateOfBirth = this.statefulSvc.wrap(dobStoragePath, undefined, pageIterationId).valueString;
    const addressroot = `${protocol}borrowerInfo/addresses`;
    const ssProtocol = pageIterationId != undefined ? `ss:coBorrowers/${pageIterationId}/` : 'ss:';
    const length = StatefulValue.arraySize(addressroot);
    let totalmonth = 0;
    for (let i = 0; i < length; i++) {
      const month = this.statefulSvc.wrap(`${addressroot}/${i}/timeinProfessionMonths`).valueNumberOnly;
      const year = this.statefulSvc.wrap(`${addressroot}/${i}/timeinProfessionYears`).valueNumberOnly;
      totalmonth += month;
      totalmonth += year * 12;
    }
    const ageinMonth = this.monthsBtwnDates(dateOfBirth);

    for (let i = 0; i < length; i++) {
      const ssStorge = `${ssProtocol}borrowerInfo/alertYearResidence/${i}/message`;
      const result = ageinMonth >= totalmonth;
      this.statefulSvc.wrap(ssStorge).value = result;
    }

    return ageinMonth >= totalmonth;
  };

  // * Validation for residence duration as configured in Admin Portal * //
  // * This function is only used for OLA for now, if it is used for OAO also it will need to be refactored * //
  public addressMinimumTwoYearValidation = (_context: LogicFnContext): boolean => {
    const pageIterationId = getPageIterationId(_context);
    const protocol = pageIterationId != undefined ? `ola:coBorrowers/${pageIterationId}/` : 'ola:';
    const addressroot = `${protocol}borrowerInfo/addresses`;
    /// skiping page validation if sameAsPrimaryAddress is checked for co-applicant
    if (pageIterationId != undefined) {
      const isSameAsPrimaryFromBundle = this.statefulSvc.wrap(`${protocol}borrowerInfo/sameAsPrimaryAddress`).value;
      if (isSameAsPrimaryFromBundle) {
        return true;
      }
    }
    const length = StatefulValue.arraySize(addressroot);
    let totalmonth = 0;
    for (let i = 0; i < length; i++) {
      const month = this.statefulSvc.wrap(`${addressroot}/${i}/timeinProfessionMonths`).valueNumberOnly;
      const year = this.statefulSvc.wrap(`${addressroot}/${i}/timeinProfessionYears`).valueNumberOnly;
      totalmonth += month;
      totalmonth += year * 12;
    }
    // check if total months is more or greater than admin configured value; default to 24 months if not configured
    return totalmonth >= (this.selectedLoanProductConfig()?.minMonthResidence || 24);
  };

  // * Validation for employment duration as configured in Admin Portal * //
  // * This function is only used for OLA for now, if it is used for OAO also it will need to be refactored * //
  public employmentMinimumTwoYearValidation = (_context: LogicFnContext, root: string = 'employments'): boolean => {
    const pageIterationId = getPageIterationId(_context);
    const protocol = pageIterationId != undefined ? `ola:coBorrowers/${pageIterationId}/` : 'ola:';
    const employmentroot = `${protocol}${root}`;
    const length = StatefulValue.arraySize(employmentroot);
    let totalmonth = 0;
    for (let i = 0; i < length; i++) {
      const month = this.statefulSvc.wrap(`${employmentroot}/${i}/timeinProfessionMonths`).valueNumberOnly;
      const year = this.statefulSvc.wrap(`${employmentroot}/${i}/timeinProfessionYears`).valueNumberOnly;
      totalmonth += month;
      totalmonth += year * 12;
    }
    // check if total months is more or greater than admin configured value; default to 24 months if not configured
    return totalmonth >= (this.selectedLoanProductConfig()?.minMonthEmployment || 24);
  };

  /**
   * DOB and Employment year validation
   *
   * @param _context The context object containing iteration and page information.
   * @returns `true` if the employment years are valid; otherwise, `false`
   */
  public isValidEmploymentYear = (_context: LogicFnContext, root: string = 'employments'): boolean => {
    const pageIterationId = getPageIterationId(_context);
    const protocol = pageIterationId != undefined ? `ola:coBorrowers/${pageIterationId}/` : 'ola:';
    const dobStoragePath = `${protocol}borrowerInfo/dateOfBirth`;

    const dateOfBirth = this.statefulSvc.wrap(dobStoragePath, undefined, pageIterationId).valueString;
    const employmentroot = `${protocol}${root}`;
    const ssProtocol = pageIterationId != undefined ? `ss:coBorrowers/${pageIterationId}/` : 'ss:';
    const length = StatefulValue.arraySize(employmentroot);
    let totalmonth = 0;
    for (let i = 0; i < length; i++) {
      const month = this.statefulSvc.wrap(`${employmentroot}/${i}/timeinProfessionMonths`).valueNumberOnly;
      const year = this.statefulSvc.wrap(`${employmentroot}/${i}/timeinProfessionYears`).valueNumberOnly;
      totalmonth += month;
      totalmonth += year * 12;
    }
    const ageinMonth = this.monthsBtwnDates(dateOfBirth);

    for (let i = 0; i < length; i++) {
      const ssStorge = `${ssProtocol}borrowerInfo/alertYearResidence/${i}/message`;
      const result = ageinMonth >= totalmonth;
      this.statefulSvc.wrap(ssStorge).value = result;
    }

    return ageinMonth >= totalmonth;
  };

  /**
   * DOB and Residence year validation message visibility
   *
   * @param _context The context object containing iteration and page information.
   * @returns `true` if the employment years are valid; otherwise, `false`
   */
  public showResidentalError = (_context: LogicFnContext): boolean => {
    return !this.isValidResidenceLivingYear(_context);
  };

  /**
   * DOB and Employment year validation message visibility
   *
   * @param _context The context object containing iteration and page information.
   * @returns `true` if the employment years are valid; otherwise, `false`
   */
  public showEmploymentError = (_context: LogicFnContext, root: string = 'employments'): boolean => {
    return !this.isValidEmploymentYear(_context, root);
  };
  private monthsBtwnDates = (date: string): number => {
    const endDate = new Date();
    const startDate = new Date(date);

    return Math.max((endDate.getFullYear() - startDate.getFullYear()) * 12 + endDate.getMonth() - startDate.getMonth(), 0);
  };

  // This Method serves for alert validation on employment, additional employment, and residence card
  public showInvalidPeriodError = (_context: LogicFnContext, root: string): boolean => {
    const pageIterationId = getPageIterationId(_context);
    const protocol = pageIterationId != undefined ? `ola:coBorrowers/${pageIterationId}/` : 'ola:';
    const addressroot = `${protocol}${root}`;

    const yearValue = this.statefulSvc.wrap(`${addressroot}/${_context.field.iterationId}/timeinProfessionYears`).valueStringOnly;
    const monthValue = this.statefulSvc.wrap(`${addressroot}/${_context.field.iterationId}/timeinProfessionMonths`).valueStringOnly;

    if (yearValue && Number(yearValue) === 0 && !monthValue) {
      return false;
    }

    if (yearValue && monthValue) {
      let totalmonth = 0;

      const month = this.statefulSvc.wrap(`${addressroot}/${_context.field.iterationId}/timeinProfessionMonths`).valueNumberOnly;
      const year = this.statefulSvc.wrap(`${addressroot}/${_context.field.iterationId}/timeinProfessionYears`).valueNumberOnly;
      totalmonth += month;
      totalmonth += year * 12;
      return !(totalmonth < 1);
    }
    return true;
  };

  // Prefixed this will ola as to not clash with a near identical one in oao-on-click-functions
  // We need to dedupe and consolidate onclick functions into 1 file
  public isValidEligibility = (_context: LogicFnContext, memberField: string, eligibilityField: string, flowType?: string): boolean => {
    // OLA & OAO
    const field = this.statefulSvc.wrap(memberField).value;
    if (field === true) {
      return true;
    }
    // check OLA valid eligibilty questions is disabled
    if (!this.adminPortalFeaturesService?.config?.olaFeatures?.canShowEligibilityQuestions && flowType?.toLowerCase() === 'ola') {
      return true;
    }
    // check OAO valid eligibilty questions is disabled
    if (this.adminPortalFeaturesService?.config?.oaoFeatures?.canShowEligibilityQuestions === false && flowType?.toLowerCase() === 'oao') {
      return true;
    }
    return this.statefulSvc.wrap(eligibilityField).value === true;
  };

  public isIdentityVerificationValid = (): boolean => {
    if (this.statefulSvc.wrap('ss:oaoMemberInformation/identityVerificationQuestions').value) {
      const identityVerificationQuestions = JSON.parse(
        this.statefulSvc.wrap('ss:oaoMemberInformation/identityVerificationQuestions').value.toString()
      );
      if (identityVerificationQuestions && identityVerificationQuestions.oowQuestion) {
        if (
          identityVerificationQuestions.oowQuestion.length ===
          identityVerificationQuestions.oowQuestion.filter((t) => {
            if (t.answer) {
              return t;
            }
          }).length
        ) {
          return true;
        } else {
          return false;
        }
      } else {
        return true;
      }
    } else {
      return true;
    }
  };

  public isAlloyVerificationValid = (): boolean => {
    return this.alloySvc.isAlloyValid();
  };

  /**
   * Can Show econsent splash screen?
   * @returns boolean
   */
  public showEConsentSplashScreen = (_context: LogicFnContext, flowType: string): boolean => {
    const primaryConsent = this.adminPortalFeaturesService?.config?.consentManagement?.primaryAcknowledgement;
    if (primaryConsent?.enableConsentScreen) {
      if (this.allRequiredEconsentAccepted(_context, flowType)) {
        return false; // we only want to show once
      }
      return true;
    }
    // ensure always false if splash screen not enabled
    return false;
  };

  /**
   * This is for the splash-screen econsent, checks against primary and additional required consents.
   * @returns True if valid otherwise false
   */
  public allRequiredEconsentAccepted = (_context: LogicFnContext, flowType: string): boolean => {
    let primaryAck: PrimitiveType = false;
    // Exclude Card without primary or additional econsent;
    if (!!_context.card?.fields && !_context.card?.fields?.filter((t) => t.visible && !!t.config.payload?.consentType).length) {
      return true;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let additionalAcceptedAcks: any[];
    // get additional econsents for primary
    const sourceAcks = this.adminPortalFeaturesService.config.consentManagement.additionalAcknowledgements ?? [];
    if ((_context.page ?? _context.card)?.config?.id.includes('#')) {
      // co-applicant, etc.
      const addRes = this.allRequiredEconsentAcceptedCoApplicant(_context, flowType);
      primaryAck = addRes[0];
      additionalAcceptedAcks = addRes[1];
    } else if (
      flowType === 'oao' &&
      ((_context.page ?? _context.card)?.config?.id.includes('co-applicant') ||
        (_context.page ?? _context.card)?.config?.id.includes('additional-co-app'))
    ) {
      const propName = (_context.page ?? _context.card)?.config?.id.includes('co-applicant') ? 'coApplicant' : 'additionalCoApplicant';
      // get primary econsent
      primaryAck = this.statefulSvc.wrap(`api:oaoMemberInformation/${propName}/consent/isAccepted`).value;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      additionalAcceptedAcks = this.stateManager.getValue<Array<any>>(`api:oaoMemberInformation/${propName}/additionalConsents`);
    } else {
      const addRes = this.allRequiredEconsentAcceptedPrimaryApplicant(_context, flowType);
      primaryAck = addRes[0];
      additionalAcceptedAcks = addRes[1];
    }

    if (additionalAcceptedAcks && Array.isArray(additionalAcceptedAcks)) {
      for (const ack of sourceAcks) {
        const match = additionalAcceptedAcks.find((a) => a && a.acknowledgeId === ack.acknowledgeId);
        if (ack.required && !match?.isAccepted) {
          return false;
        }
      }
    }
    // we make it this far, all additional required are accepted
    return primaryAck === 'true' || primaryAck === true;
  };

  /**
   * Does the fetch logic for primary applicants
   * @param _context The function logic context
   * @param flowType The flow type we are using
   * @returns primary accepted along with an array of additional to check
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private allRequiredEconsentAcceptedPrimaryApplicant = (_context: LogicFnContext, flowType: string): [PrimitiveType, any[]] => {
    let primaryAck: PrimitiveType = false;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let additionalAcceptedAcks: any[];
    if (flowType === 'oao') {
      const isBusinessMembership = (_context.page ?? _context.card)?.flowId?.includes('business') ?? false;
      if (isBusinessMembership) {
        // get primary econsent
        primaryAck = this.statefulSvc.wrap('api:oaoMemberInformation/businessInformation/responsibleParties/0/consent/isAccepted').value;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        additionalAcceptedAcks = this.stateManager.getValue<Array<any>>(
          'api:oaoMemberInformation/businessInformation/responsibleParties/0/additionalConsents'
        );
      } else {
        // get primary econsent
        primaryAck = this.statefulSvc.wrap('api:oaoMemberInformation/primaryApplicant/consent/isAccepted').value;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        additionalAcceptedAcks = this.stateManager.getValue<Array<any>>('api:oaoMemberInformation/primaryApplicant/additionalConsents');
      }
    } else {
      // get primary econsent
      primaryAck = this.statefulSvc.wrap('ola:consent/isAccepted').value;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      additionalAcceptedAcks = this.stateManager.getValue<Array<any>>('ola:additionalConsents');
    }
    return [primaryAck, additionalAcceptedAcks];
  };

  /**
   * Does the fetch logic for additional applicants
   * @param _context The function logic context
   * @param flowType The flow type we are using
   * @returns primary accepted along with an array of additional to check
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private allRequiredEconsentAcceptedCoApplicant = (_context: LogicFnContext, flowType: string): [PrimitiveType, any[]] => {
    let primaryAck: PrimitiveType = false;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let additionalAcceptedAcks: any[];
    const pageIter = (_context.page ?? _context.card)?.pageIterationId;
    if (flowType === 'oao') {
      const isBusinessMembership = (_context.page ?? _context.card)?.flowId?.includes('business') ?? false;
      if (isBusinessMembership) {
        // get primary econsent
        primaryAck = this.statefulSvc.wrap(
          'api:oaoMemberInformation/businessInformation/responsibleParties/#/consent/isAccepted',
          null,
          pageIter
        ).value;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        additionalAcceptedAcks = this.stateManager.getValue<Array<any>>(
          `api:oaoMemberInformation/businessInformation/responsibleParties/${pageIter - 1}/additionalConsents`
        );
      } else {
        const propName = pageIter === 1 ? 'coApplicant' : 'additionalCoApplicant';
        // get primary econsent
        primaryAck = this.statefulSvc.wrap(`api:oaoMemberInformation/${propName}/consent/isAccepted`).value;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        additionalAcceptedAcks = this.stateManager.getValue<Array<any>>(`api:oaoMemberInformation/${propName}/additionalConsents`);
      }
    } else {
      // get primary econsent
      primaryAck = this.statefulSvc.wrap('ola:coBorrowers/#/consent/isAccepted', null, pageIter).value;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      additionalAcceptedAcks = this.stateManager.getValue<Array<any>>(`ola:coBorrowers/${pageIter}/additionalConsents`);
    }
    return [primaryAck, additionalAcceptedAcks];
  };

  /**
   * Provides an inverse function for showing consent cards when in incomplete or new state.
   * Note: if we want to include optional validation we should rewrite this method, or remove entirely/make dummy.
   * @param _context The context of the flow and page/card
   * @param flowType The flow currently used - determines what fields to look at
   * @returns true if incomplete or not cached, false if all required are completed and cached
   */
  public consentCachedAlready = (_context: LogicFnContext, flowType: string): boolean => {
    const state = this.allRequiredEconsentAccepted(_context, flowType);
    // set during bundle generation
    const protocol = flowType === 'ola' ? 'ola' : 'api';
    const prefill = this.stateManager.getValue<boolean>(`${protocol}:consentAcceptenceSnapshotPrefill`);
    return !state || !prefill;
  };

  /**
   * Validate Consent Card
   * @param _context Page Context
   * @param consentCardId Consent Card Id
   * @param econsentPath eConsent Path
   * @param additionaconsentPath additional Consent
   * @returns boolean
   */
  public validateRequiredConsentsCardAccepted = (
    _context: LogicFnContext,
    consentCardId: string,
    econsentPath: string,
    additionaconsentPath: string
  ): boolean => {
    // Get Consent Card & check visibility
    const consentCard = this.cardService.createCard(consentCardId, _context.page.flowId, null, _context.page.pageIterationId);

    // If Visible then validate the field else skip it
    if (consentCard.visible) {
      const sourceAcks =
        this.adminPortalFeaturesService.config.consentManagement.additionalAcknowledgements?.filter((a) => a.required) ?? [];
      // EConsent
      const primaryAck = this.statefulSvc.wrap(processPath(econsentPath, _context)).value;
      const addcon = processPath(additionaconsentPath, _context);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const additionalAcceptedAcks = this.stateManager.getValue<Array<any>>(addcon);

      if (additionalAcceptedAcks && Array.isArray(additionalAcceptedAcks)) {
        for (const ack of sourceAcks) {
          const match = additionalAcceptedAcks.find((a) => a && a.acknowledgeId === ack.acknowledgeId);
          if (ack.required && (!match?.isAccepted || match === undefined)) {
            return false;
          }
        }
      }
      // we make it this far, all additional required are accepted
      return primaryAck === 'true' || primaryAck === true;
    } else {
      return true;
    }
  };
}
